import type { TextFieldProps } from '@mui/material';
import TextField from '@mui/material/TextField';
import {
  Control,
  Controller,
  ControllerProps,
  FieldError,
  Path
} from 'react-hook-form';
import { FieldValues } from 'react-hook-form/dist/types/fields';
import { useTranslation } from 'react-i18next';

export type TextFieldElementProps<T> = Omit<TextFieldProps, 'name'> & {
  validation?: ControllerProps['rules'];
  name: Path<T>;
  parseError?: (error: FieldError) => string;
  control?: Control<T>;
  requiredAndCaracter?: boolean;
  requiredAndDayNatural?: boolean;
  customValue?: string;
};

export function TextFieldElement<TFieldValues extends FieldValues>({
  validation = {},
  parseError,
  type,
  required,
  requiredAndCaracter,
  requiredAndDayNatural,
  name,
  control,
  customValue = null,
  ...rest
}: TextFieldElementProps<TFieldValues>) {
  const { t, i18n } = useTranslation('global');
  if (required && !validation.required) {
    validation.required = t('common.commonMessages.requiredField');
  }

  if (requiredAndCaracter && !validation.required) {
    validation.required = t('admin.catalogs.currency.requiredField');
  }

  if (requiredAndDayNatural && !validation.required) {
    validation.required = t('common.commonMessages.requiredAndDayNatural');
  }

  if (type === 'email' && !validation.pattern) {
    validation.pattern = {
      value:
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      message: 'Formato inválido'
    };
  }

  return (
    <Controller
      control={control}
      name={name}
      render={({
        field: { value, onChange, onBlur },
        fieldState: { invalid, error }
      }) => (
        <TextField
          {...rest}
          error={invalid}
          helperText={
            error
              ? typeof parseError === 'function'
                ? parseError(error)
                : error.message
              : rest.helperText
          }
          inputProps={{
            lang: i18n.language
          }}
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          sx={{
            ...(rest?.sx ?? {}),
            textarea: {
              padding: '5px'
            }
          }}
          type={type}
          value={customValue ?? value ?? ''}
          variant={rest?.variant || 'standard'}
        />
      )}
      rules={validation}
    />
  );
}

export default TextFieldElement;
